import { surveyApi } from '../config/apiClient';

// ============================================================================
// surveyDataService — gts_survey_api (SQL) icin tek REST yuzeyi (Faz 8 — Anket SQL).
//
// RTDB `companies/{cid}/surveys` + `answeredSurveys` + Node `/api/surveys/*`
// uclarinin yerine gecer. Authorization + X-Company-Id header'lari apiClient
// interceptor'i tarafindan otomatik eklenir (giris yapmis admin akislari).
//
// PUBLIC (anonim QR) akisi: QR URL yalniz taskId tasir → once resolveCompany()
// ile companyId cozulur, sonra getPublicSurveysByTask/submitPublicSurvey
// cagrilarinda X-Company-Id EXPLICIT gonderilir (anonim sayfada token/claim yok).
//
// Backend (System.Text.Json) camelCase doner.
// Uclar: SurveyController (/api/Survey, /api/Survey/public/*),
//        SurveyStatsController (/api/Survey/stats/*).
// ============================================================================

export type SurveyAnswerType = 'positive' | 'negative' | 'neutral';

export interface SurveyAnswerOption {
  text: string;
  type: SurveyAnswerType;
}

/** RTDB-uyumlu anket sekli. `id` = SQL int (string'e cevrilir); legacyFirebaseKey ayri tutulur. */
export interface SurveyDto {
  id: string;
  sqlId: number;
  legacyFirebaseKey?: string | null;
  title: string;
  branchId?: string | null;
  createdBy?: string | null;
  createdAt: number;
  answers: SurveyAnswerOption[];
  tasks: string[];
}

export interface SurveyListParams {
  branchId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface SurveyListResult {
  items: SurveyDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface SurveyCreateInput {
  title: string;
  branchId?: string | null;
  answers: SurveyAnswerOption[];
  createdByUid?: string | null;
  tasks?: string[];
}

export interface SurveyUpdateInput {
  id: number;
  title?: string;
  branchId?: string | null;
  answers?: SurveyAnswerOption[];
}

export interface SurveyStatsFilter {
  branchId?: string;
  taskFirebaseKey?: string;
  surveyId?: number;
  answerType?: string;
  startDate?: string; // yyyy-MM-dd
  endDate?: string;   // yyyy-MM-dd
}

export interface SurveySummary {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
  satisfactionRate: number;
  dissatisfactionRate: number;
  neutralRate: number;
}

export interface SurveyTrendPoint {
  week: string;
  positivePercent: number;
  neutralPercent: number;
  negativePercent: number;
  total: number;
}

export interface SurveyBySurveyStat {
  surveyId: number;
  surveyTitle: string;
  totalResponses: number;
  totalParticipations: number;
  satisfactionRate: number;
  dissatisfactionRate: number;
  neutralRate: number;
  feedbackCount: number;
  lastResponseDate: string | null;
  averageResponsesPerParticipation: number;
}

export interface SurveyReportRow {
  responseId: number;
  taskFirebaseKey: string;
  taskName?: string | null;
  branchId?: string | null;
  surveyId?: number | null;
  surveyTitle?: string | null;
  questionTitle?: string | null;
  answer?: string | null;
  answerType?: string | null;
  createdAt: string;
  feedback?: string | null;
  personnelName?: string | null;
}

export interface SurveyReportResult {
  items: SurveyReportRow[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// ----------------------------------------------------------------------------
// Yardimcilar
// ----------------------------------------------------------------------------

function toEpoch(value: any): number {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function normalizeAnswers(raw: any): SurveyAnswerOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a) => a && typeof a.text === 'string')
    .map((a) => ({
      text: a.text,
      type: (a.type === 'positive' || a.type === 'negative' ? a.type : 'neutral') as SurveyAnswerType,
    }));
}

function mapSurvey(raw: any): SurveyDto {
  return {
    id: String(raw.id),
    sqlId: Number(raw.id),
    legacyFirebaseKey: raw.legacyFirebaseKey ?? null,
    title: raw.title || '',
    branchId: raw.branchId ?? undefined,
    createdBy: raw.createdBy ?? null,
    createdAt: toEpoch(raw.createdAt),
    answers: normalizeAnswers(raw.answers),
    tasks: Array.isArray(raw.tasks) ? raw.tasks : [],
  };
}

const BASE = '/api/Survey';
const STATS = '/api/Survey/stats';

export const surveyDataService = {
  // ── Admin CRUD ────────────────────────────────────────────────────────
  async listSurveys(params: SurveyListParams = {}): Promise<SurveyListResult> {
    const { data } = await surveyApi.get(BASE, { params });
    const items = Array.isArray(data?.data) ? data.data.map(mapSurvey) : [];
    return {
      items,
      totalCount: data?.totalCount ?? items.length,
      page: data?.page ?? 1,
      pageSize: data?.pageSize ?? 20,
    };
  },

  /** Tum anketleri (sayfalari dolasarak) tek listede getirir. */
  async listAllSurveys(params: Omit<SurveyListParams, 'page' | 'pageSize'> = {}): Promise<SurveyDto[]> {
    const all: SurveyDto[] = [];
    let page = 1;
    for (;;) {
      const res = await this.listSurveys({ ...params, page, pageSize: 200 });
      all.push(...res.items);
      if (res.items.length === 0 || all.length >= (res.totalCount || 0)) break;
      page++;
    }
    return all;
  },

  async getSurvey(id: number): Promise<SurveyDto | null> {
    const { data } = await surveyApi.get(`${BASE}/${id}`);
    return data?.data ? mapSurvey(data.data) : null;
  },

  async createSurvey(input: SurveyCreateInput): Promise<{ id: number; createdAt: string } | null> {
    const { data } = await surveyApi.post(`${BASE}/create`, input);
    return data?.data ?? null;
  },

  async updateSurvey(input: SurveyUpdateInput): Promise<void> {
    await surveyApi.post(`${BASE}/update`, input);
  },

  async deleteSurvey(id: number): Promise<void> {
    await surveyApi.post(`${BASE}/delete`, { id });
  },

  /** Ankete bagli gorevleri (taskFirebaseKey listesi) komple ayarlar. */
  async linkSurveyTasks(surveyId: number, taskFirebaseKeys: string[]): Promise<void> {
    await surveyApi.post(`${BASE}/${surveyId}/tasks`, { tasks: taskFirebaseKeys });
  },

  // ── Public (anonim QR) ────────────────────────────────────────────────
  /** taskId → companyId (merkezi resolver; anonim, header gerektirmez). Bulunamazsa null. */
  async resolveCompany(taskFirebaseKey: string): Promise<string | null> {
    const { data } = await surveyApi.get(`${BASE}/public/resolve/${encodeURIComponent(taskFirebaseKey)}`);
    return data?.data?.companyId ?? null;
  },

  async getPublicSurveysByTask(taskFirebaseKey: string, companyId: string): Promise<SurveyDto[]> {
    const { data } = await surveyApi.get(`${BASE}/public/task/${encodeURIComponent(taskFirebaseKey)}`, {
      headers: { 'X-Company-Id': companyId },
    });
    const surveys = data?.data?.surveys;
    return Array.isArray(surveys) ? surveys.map(mapSurvey) : [];
  },

  async submitPublicSurvey(
    taskFirebaseKey: string,
    companyId: string,
    payload: {
      answers: { [surveyId: string]: { questionTitle: string; selectedAnswer: string; answerType: string } };
      feedback?: string | null;
    }
  ): Promise<void> {
    await surveyApi.post(`${BASE}/public/submit/${encodeURIComponent(taskFirebaseKey)}`, payload, {
      headers: { 'X-Company-Id': companyId },
    });
  },

  // ── Istatistik / rapor ────────────────────────────────────────────────
  async getSummary(filter: SurveyStatsFilter = {}): Promise<SurveySummary> {
    const { data } = await surveyApi.get(`${STATS}/summary`, { params: filter });
    return (
      data?.data ?? {
        positive: 0, neutral: 0, negative: 0, total: 0,
        satisfactionRate: 0, dissatisfactionRate: 0, neutralRate: 0,
      }
    );
  },

  async getTrend(filter: SurveyStatsFilter & { weeks?: number } = {}): Promise<SurveyTrendPoint[]> {
    const { data } = await surveyApi.get(`${STATS}/trend`, { params: filter });
    return Array.isArray(data?.data) ? data.data : [];
  },

  async getBySurvey(filter: Omit<SurveyStatsFilter, 'surveyId'> = {}): Promise<SurveyBySurveyStat[]> {
    const { data } = await surveyApi.get(`${STATS}/by-survey`, { params: filter });
    return Array.isArray(data?.data) ? data.data : [];
  },

  async getReport(filter: SurveyStatsFilter & { page?: number; pageSize?: number } = {}): Promise<SurveyReportResult> {
    const { data } = await surveyApi.get(`${STATS}/report`, { params: filter });
    return {
      items: Array.isArray(data?.data) ? data.data : [],
      totalCount: data?.totalCount ?? 0,
      page: data?.page ?? 1,
      pageSize: data?.pageSize ?? 30,
    };
  },

  /** Tum rapor satirlarini (sayfalari dolasarak) getirir — client-side filtre/Excel icin. */
  async getAllReportRows(filter: SurveyStatsFilter = {}): Promise<SurveyReportRow[]> {
    const all: SurveyReportRow[] = [];
    let page = 1;
    for (;;) {
      const res = await this.getReport({ ...filter, page, pageSize: 500 });
      all.push(...res.items);
      if (res.items.length === 0 || all.length >= (res.totalCount || 0)) break;
      page++;
    }
    return all;
  },
};

export default surveyDataService;
